import React, { Component } from "react";
import {
  Col,
  Row,
  FormGroup,
  Label,
  Modal,
  Input,
} from "reactstrap";
import {
  getTeamMembers,
  getAvailableModules,
  createTeamMember,
  updateTeamMember,
  updateTeamPermissions,
  deleteTeamMember,
  resetTeamMemberPassword,
  getActiveBranches,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Table, Chip, Btn, IconBtn, Avatar, Field, MeterList, PanelLink, DialogBtn, ConfirmDialog,
} from "../../components/Console/ConsoleUI";
import {
  isBranchScopedAdmin,
  getUserModulePermissions,
  canWrite,
  isSuperAdmin,
} from "../../services/PermissionService";

/**
 * Team & access (F5 of the handoff).
 *
 * The design's right rail carries a branch list and a live activity feed. The
 * branch list is real (getActiveBranches, with the staff count computed from
 * the members already loaded); there is no team-scoped activity endpoint, so
 * that panel is replaced by an access summary drawn from the same members and
 * a link to the real activity log.
 *
 * Permission rules are unchanged and deliberately conservative: TEAM is never
 * grantable, and a branch-scoped admin can only hand out modules they hold
 * themselves. The server enforces both — this only stops the UI offering what
 * would be rejected.
 */

// Module Categories Configuration
const MODULE_CATEGORIES = {
  dashboard: {
    name: "Dashboard & Analytics",
    icon: "grid_view",
    modules: ["DASHBOARD", "ANALYTICS"]
  },
  kitchen: {
    name: "Kitchen & Food",
    icon: "restaurant",
    modules: ["FOOD"]
  },
  tableBooking: {
    name: "Table Booking",
    icon: "table_restaurant",
    modules: ["TABLE"]
  },
  rooms: {
    name: "Room Management",
    icon: "bed",
    modules: ["ROOM"]
  },
  facilities: {
    name: "Facilities & Services",
    icon: "attractions",
    modules: ["ATTRACTION", "TAXI"]
  },
  transport: {
    name: "Transportation",
    icon: "local_taxi",
    modules: ["TRANSPORTATION", "DRIVERS"]
  },
  users: {
    name: "User Management",
    icon: "group",
    modules: ["USERS"]
  },
  communication: {
    name: "Communication",
    icon: "forum",
    modules: ["CHAT", "NOTIFICATION"]
  },
  marketing: {
    name: "Marketing & Promotions",
    icon: "sell",
    modules: ["COUPON", "BANNER"]
  },
  system: {
    name: "System",
    icon: "settings",
    modules: ["SETTINGS"]
  },
};

const CSS = `
.tm-check{display:flex;align-items:flex-start;gap:10px;padding:9px 11px;border-radius:11px;
  cursor:pointer;border:1px solid transparent;background:${C.surface}}
.tm-check:hover{border-color:${C.border}}
.tm-check.is-on{background:#fff;border-color:${C.sand}}
.tm-box{width:19px;height:19px;flex:0 0 19px;border-radius:6px;border:1.5px solid ${C.sand};
  background:#fff;display:grid;place-items:center;margin-top:1px}
.tm-box.is-on{background:${C.brown};border-color:${C.brown};color:#fff}
.tm-box.is-part{background:${C.sand};border-color:${C.sand};color:${C.brown}}
.tm-sel{height:38px;padding:0 12px;border-radius:19px;border:1px solid ${C.border};
  background:#fff;color:${C.taupe};font-size:14px;font-family:inherit;outline:none}
.tm-pw{width:100%;height:46px;padding:0 46px 0 15px;background:#fff;border:1px solid #e6d8c4;
  border-radius:12px;font-size:15px;color:${C.ink};font-family:inherit;outline:none}
.tm-pw:focus{border-color:${C.sand}}
.tm-eye{position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;
  border:0;cursor:pointer;padding:6px;color:${C.taupeLight};line-height:1}
`;

// A checkbox on the console surface. Bootstrap's custom-control is a grey
// template artefact; this is the design's square.
const Box = ({ on, part }) => (
  <span className={`tm-box ${on ? "is-on" : part ? "is-part" : ""}`}>
    {(on || part) && (
      <span className="ms" style={{ fontSize: 14 }}>{on ? "check" : "remove"}</span>
    )}
  </span>
);

class TeamList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      modules: [],
      branches: [],
      branchFilter: "", // list scope: "" = all branches
      branchId: "", // create form: branch this member is scoped to
      isLoaded: false,
      modal: false,
      permissionModal: false,
      passwordModal: false,

      // Form fields
      memberId: "",
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      password: "",
      // "full" or "view" — view is the Branch Staff tier (read-only).
      accessLevel: "full",
      selectedModules: [],
      newPassword: "",
      confirmPassword: "",
      showNewPassword: false,
      showConfirmPassword: false,
      passwordResetSuccess: false,
      resetMemberName: "",

      // Delete modal states
      showDeleteModal: false,
      selectedMemberId: null,
      deleteLoading: false,
    };

    this.toggle = this.toggle.bind(this);
    this.togglePermission = this.togglePermission.bind(this);
    this.togglePassword = this.togglePassword.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleModuleChange = this.handleModuleChange.bind(this);
  }

  toggle(resetFields = true) {
    if (resetFields) {
      this.setState({
        modal: !this.state.modal,
        memberId: "",
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        password: "",
        accessLevel: "full",
        selectedModules: [],
        branchId: "",
      });
    } else {
      this.setState({ modal: !this.state.modal });
    }
  }

  togglePermission() {
    this.setState({ permissionModal: !this.state.permissionModal });
  }

  togglePassword() {
    this.setState({
      passwordModal: !this.state.passwordModal,
      newPassword: "",
      confirmPassword: "",
      showNewPassword: false,
      showConfirmPassword: false,
      passwordResetSuccess: false,
    });
  }

  // Delete modal toggle
  toggleDeleteModal = () => this.setState({ showDeleteModal: !this.state.showDeleteModal });

  handleChange = (e) => {
    this.setState({ [e.target.id]: e.target.value });
  };

  handleModuleChange = (moduleId) => {
    const { selectedModules } = this.state;
    if (selectedModules.includes(moduleId)) {
      this.setState({
        selectedModules: selectedModules.filter((id) => id !== moduleId),
      });
    } else {
      this.setState({
        selectedModules: [...selectedModules, moduleId],
      });
    }
  };

  // Handle category select all
  handleCategorySelectAll = (categoryKey) => {
    const { selectedModules, modules } = this.state;
    const category = MODULE_CATEGORIES[categoryKey];
    const categoryModuleIds = category.modules.filter(modId =>
      modules.some(m => m.moduleId === modId)
    );

    const allSelected = categoryModuleIds.every(modId => selectedModules.includes(modId));

    if (allSelected) {
      // Deselect all in category
      this.setState({
        selectedModules: selectedModules.filter(id => !categoryModuleIds.includes(id))
      });
    } else {
      // Select all in category
      const newSelected = [...new Set([...selectedModules, ...categoryModuleIds])];
      this.setState({ selectedModules: newSelected });
    }
  };

  // Handle master select all
  handleSelectAll = () => {
    const { selectedModules, modules } = this.state;
    const allModuleIds = modules.map(m => m.moduleId);

    if (selectedModules.length === allModuleIds.length) {
      this.setState({ selectedModules: [] });
    } else {
      this.setState({ selectedModules: allModuleIds });
    }
  };

  // Check if category is fully selected
  isCategoryFullySelected = (categoryKey) => {
    const { selectedModules, modules } = this.state;
    const category = MODULE_CATEGORIES[categoryKey];
    const categoryModuleIds = category.modules.filter(modId =>
      modules.some(m => m.moduleId === modId)
    );
    return categoryModuleIds.length > 0 && categoryModuleIds.every(modId => selectedModules.includes(modId));
  };

  // Check if category is partially selected
  isCategoryPartiallySelected = (categoryKey) => {
    const { selectedModules, modules } = this.state;
    const category = MODULE_CATEGORIES[categoryKey];
    const categoryModuleIds = category.modules.filter(modId =>
      modules.some(m => m.moduleId === modId)
    );
    const selectedCount = categoryModuleIds.filter(modId => selectedModules.includes(modId)).length;
    return selectedCount > 0 && selectedCount < categoryModuleIds.length;
  };

  // Get modules for a category
  getCategoryModules = (categoryKey) => {
    const { modules } = this.state;
    const category = MODULE_CATEGORIES[categoryKey];
    return modules.filter(m => category.modules.includes(m.moduleId));
  };

  handleSubmit(e) {
    e.preventDefault();
    const { memberId, firstName, lastName, email, mobile, password, accessLevel, selectedModules, branchId } = this.state;

    if (memberId) {
      // Update existing member
      updateTeamMember(memberId, { firstName, lastName, email, mobile })
        .then((response) => {
          if (response.status === 200) {
            toast.success("Team member updated successfully");
            this.getAllTeamMembers();
            this.toggle();
          } else {
            toast.error(response.message || "Failed to update team member");
          }
        })
        .catch(() => toast.error("Error updating team member"));
    } else {
      // Create new member
      createTeamMember({ firstName, lastName, email, mobile, password, accessLevel, modulePermissions: selectedModules, branchId: branchId || undefined })
        .then((response) => {
          if (response.status === 200) {
            toast.success("Team member created successfully");
            this.getAllTeamMembers();
            this.toggle();
          } else {
            toast.error(response.message || "Failed to create team member");
          }
        })
        .catch(() => toast.error("Error creating team member"));
    }
  }

  handlePermissionSubmit = () => {
    const { memberId, selectedModules } = this.state;
    updateTeamPermissions(memberId, selectedModules)
      .then((response) => {
        if (response.status === 200) {
          toast.success("Permissions updated successfully");
          this.getAllTeamMembers();
          this.togglePermission();
        } else {
          toast.error(response.message || "Failed to update permissions");
        }
      })
      .catch(() => toast.error("Error updating permissions"));
  };

  handlePasswordSubmit = () => {
    const { memberId, newPassword, confirmPassword } = this.state;

    // Validation
    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!confirmPassword.trim()) {
      toast.error("Please confirm the password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    resetTeamMemberPassword(memberId, newPassword)
      .then((response) => {
        if (response.status === 200) {
          toast.success("Password reset successfully");
          this.setState({ passwordResetSuccess: true });
        } else {
          toast.error(response.message || "Failed to reset password");
        }
      })
      .catch(() => toast.error("Error resetting password"));
  };

  copyPasswordToClipboard = () => {
    const { newPassword } = this.state;
    navigator.clipboard.writeText(newPassword).then(() => {
      toast.success("Password copied to clipboard");
    }).catch(() => {
      toast.error("Failed to copy password");
    });
  };

  getAllTeamMembers() {
    getTeamMembers(this.state.branchFilter || undefined)
      .then((result) => {
        if (result.status === 200) {
          this.setState({
            isLoaded: true,
            items: result.data,
          });
        }
      })
      .catch(() => toast.error("Error fetching team members"));
  }

  getAllModules() {
    getAvailableModules()
      .then((result) => {
        if (result.status === 200) {
          // Filter out TEAM module (delegated managers can't mint team managers).
          let modules = result.data.filter((m) => m.moduleId !== "TEAM");
          // Branch-scoped admins can only grant a subset of their OWN permissions.
          if (isBranchScopedAdmin()) {
            const own = getUserModulePermissions();
            modules = modules.filter((m) => own.includes(m.moduleId));
          }
          this.setState({ modules });
        }
      })
      .catch(() => toast.error("Error fetching modules"));
  }

  editMember(member) {
    this.setState({
      memberId: member._id,
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      mobile: member.mobile,
      selectedModules: member.modulePermissions || [],
    }, () => {
      this.toggle(false); // Don't reset fields when editing
    });
  }

  openPermissions(member) {
    this.setState({
      memberId: member._id,
      selectedModules: member.modulePermissions || [],
    });
    this.togglePermission();
  }

  openPasswordReset(member) {
    this.setState({
      memberId: member._id,
      resetMemberName: `${member.firstName} ${member.lastName}`,
      passwordModal: true,
      newPassword: "",
      confirmPassword: "",
      showNewPassword: false,
      showConfirmPassword: false,
      passwordResetSuccess: false,
    });
  }

  deleteMember(id) {
    this.setState({ selectedMemberId: id, showDeleteModal: true });
  }

  confirmDelete = () => {
    const { selectedMemberId } = this.state;
    this.setState({ deleteLoading: true });
    deleteTeamMember(selectedMemberId)
      .then((response) => {
        if (response.status === 200) {
          toast.success("Team member deleted successfully");
          this.getAllTeamMembers();
        } else {
          toast.error(response.message || "Failed to delete team member");
        }
      })
      .catch(() => toast.error("Error deleting team member"))
      .finally(() => {
        this.setState({ deleteLoading: false, showDeleteModal: false, selectedMemberId: null });
      });
  }

  getAllBranches = () => {
    getActiveBranches()
      .then((res) => {
        if (res && res.status === 200) this.setState({ branches: res.data || [] });
      })
      .catch(() => {});
  };

  handleBranchFilterChange = (e) => {
    this.setState({ branchFilter: e.target.value }, () => this.getAllTeamMembers());
  };

  componentDidMount() {
    this.getAllTeamMembers();
    this.getAllModules();
    this.getAllBranches();
  }

  // --- Members table --------------------------------------------------------

  columns() {
    const write = canWrite();
    return [
      {
        key: "member",
        label: "Member",
        render: (m) => (
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <Avatar name={`${m.firstName || ""} ${m.lastName || ""}`} size={34} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, color: C.ink }}>
                {`${m.firstName || ""} ${m.lastName || ""}`.trim() || "Unnamed"}
              </div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>{m.email}</div>
            </div>
          </div>
        ),
      },
      {
        key: "access",
        label: "Access",
        render: (m) => (
          <div>
            <div style={{ fontSize: 14, color: C.ink }}>
              {m.accessLevel === "view" ? "View only" : "Full access"}
            </div>
            <div style={{ fontSize: 12, color: C.taupeLight }}>
              {m.mobile || "No mobile"}
            </div>
          </div>
        ),
      },
      {
        key: "branch",
        label: "Branch",
        render: (m) =>
          m.primaryBranchId ? (
            <span style={{ fontSize: 13, color: C.taupe }}>
              {m.primaryBranchId.name || m.primaryBranchId.code}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: C.taupeLight }}>All branches</span>
          ),
      },
      {
        key: "modules",
        label: "Modules",
        render: (m) => {
          const mods = m.modulePermissions || [];
          if (mods.length === 0) {
            return <span style={{ fontSize: 13, color: C.taupeLight }}>None granted</span>;
          }
          return (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {mods.slice(0, 3).map((mod) => (
                <Chip key={mod}>{mod.replace(/_/g, " ")}</Chip>
              ))}
              {mods.length > 3 && (
                <span style={{ fontSize: 13, color: C.taupeLight, alignSelf: "center" }}>
                  +{mods.length - 3}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        render: (m) => (
          <Chip tone={m.isActive ? "good" : "bad"}>{m.isActive ? "Active" : "Inactive"}</Chip>
        ),
      },
      // Write controls stay behind canWrite(): a view-only account is rejected
      // by the server on every one of these, so offering them is a lie.
      ...(write
        ? [
            {
              key: "actions",
              label: "",
              align: "right",
              render: (m) => (
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <IconBtn icon="edit" title="Edit member" onClick={() => this.editMember(m)} />
                  <IconBtn
                    icon="shield_person"
                    title="Manage permissions"
                    onClick={() => this.openPermissions(m)}
                  />
                  <IconBtn
                    icon="key"
                    title="Reset password"
                    onClick={() => this.openPasswordReset(m)}
                  />
                  <IconBtn
                    icon="delete"
                    title="Remove member"
                    tone="bad"
                    onClick={() => this.deleteMember(m._id)}
                  />
                </div>
              ),
            },
          ]
        : []),
    ];
  }

  // --- Right rail -----------------------------------------------------------

  renderBranches() {
    const { branches, items } = this.state;
    const staffAt = (id) =>
      items.filter(
        (m) => m.primaryBranchId && String(m.primaryBranchId._id || m.primaryBranchId) === String(id)
      ).length;

    return (
      <Panel
        title="Branches"
        actions={
          isSuperAdmin() ? (
            <button
              type="button"
              onClick={() => this.props.history.push("/branches")}
              style={{
                border: 0, background: "transparent", color: C.brown,
                fontSize: 14, fontWeight: 500, fontFamily: "inherit", cursor: "pointer", padding: 0,
              }}
            >
              Manage
            </button>
          ) : null
        }
        style={{ padding: 20 }}
      >
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {branches.length === 0 ? (
            <div style={{ fontSize: 14, color: C.taupeLight }}>No active branches.</div>
          ) : (
            branches.map((b) => {
              const n = staffAt(b._id);
              return (
                <div
                  key={b._id}
                  style={{
                    display: "flex", alignItems: "center", gap: 11, padding: 12,
                    borderRadius: 13, background: C.surface,
                  }}
                >
                  <span className="ms" style={{ fontSize: 19, color: C.brown }}>apartment</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: C.ink }}>{b.name}</div>
                    <div style={{ fontSize: 12, color: C.taupeLight }}>
                      {b.code}
                      {this.state.branchFilter ? "" : ` · ${n} scoped member${n === 1 ? "" : "s"}`}
                    </div>
                  </div>
                  <Chip tone="good">Live</Chip>
                </div>
              );
            })
          )}
        </div>
      </Panel>
    );
  }

  renderAccessSummary() {
    const { items, modules } = this.state;
    const full = items.filter((m) => m.accessLevel !== "view").length;
    const view = items.length - full;
    const tenantWide = items.filter((m) => !m.primaryBranchId).length;

    const coverage = modules
      .map((mod) => {
        const n = items.filter((m) => (m.modulePermissions || []).includes(mod.moduleId)).length;
        return {
          label: mod.moduleName || mod.moduleId,
          value: `${n}`,
          pct: items.length ? (n / items.length) * 100 : 0,
          n,
        };
      })
      .sort((a, b) => b.n - a.n)
      .slice(0, 5);

    const line = (label, value) => (
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
        <span>{label}</span>
        <span style={{ color: C.ink }}>{value}</span>
      </div>
    );

    return (
      <Panel
        title="Access summary"
        subtitle="Drawn from the members listed here"
        style={{ padding: 20, display: "flex", flexDirection: "column", flex: 1 }}
        bodyStyle={{ display: "flex", flexDirection: "column", flex: 1 }}
      >
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {line("Full access", full)}
          {line("View only", view)}
          {line("Not tied to a branch", tenantWide)}
        </div>

        <div style={{ marginTop: 20, fontSize: 13, letterSpacing: ".6px", color: C.taupe, textTransform: "uppercase" }}>
          Most granted modules
        </div>
        <MeterList items={coverage} empty="No modules granted yet" />

        {isSuperAdmin() && (
          <PanelLink onClick={() => this.props.history.push("/activity-logs")}>
            Full activity log
          </PanelLink>
        )}
      </Panel>
    );
  }

  // --- Permission picker ----------------------------------------------------

  renderPermissionCategories() {
    const { selectedModules, modules } = this.state;
    const allModuleIds = modules.map(m => m.moduleId);
    const isAllSelected = allModuleIds.length > 0 && selectedModules.length === allModuleIds.length;

    return (
      <div>
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 16, padding: "16px 18px", borderRadius: 16, background: C.brown, color: "#fff",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>Module permissions</div>
            <div style={{ marginTop: 3, fontSize: 13, color: "rgba(255,255,255,.66)" }}>
              {selectedModules.length} of {allModuleIds.length} modules granted
              {isBranchScopedAdmin() && " — limited to the modules you hold yourself"}
            </div>
          </div>
          <button
            type="button"
            onClick={this.handleSelectAll}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, height: 36, padding: "0 14px",
              borderRadius: 18, border: "1px solid rgba(255,255,255,.3)", background: "transparent",
              color: "#fff", fontSize: 13, fontFamily: "inherit", cursor: "pointer", flex: "0 0 auto",
            }}
          >
            <span className="ms" style={{ fontSize: 17 }}>
              {isAllSelected ? "remove_done" : "done_all"}
            </span>
            {isAllSelected ? "Clear all" : "Select all"}
          </button>
        </div>

        {allModuleIds.length === 0 && (
          <div style={{ marginTop: 18, fontSize: 14, color: C.taupe }}>
            You do not hold any module you could pass on.
          </div>
        )}

        <div
          style={{
            marginTop: 18, display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 14,
          }}
        >
          {Object.entries(MODULE_CATEGORIES).map(([key, category]) => {
            const categoryModules = this.getCategoryModules(key);
            if (categoryModules.length === 0) return null;

            const isFullySelected = this.isCategoryFullySelected(key);
            const isPartiallySelected = this.isCategoryPartiallySelected(key);

            return (
              <div
                key={key}
                style={{
                  background: "#fff", border: `1px solid ${C.tint}`,
                  borderRadius: 16, overflow: "hidden",
                }}
              >
                <div
                  onClick={() => this.handleCategorySelectAll(key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
                    background: C.surfaceAlt, borderBottom: `1px solid ${C.tint}`, cursor: "pointer",
                  }}
                >
                  <span className="ms" style={{ fontSize: 19, color: C.brown }}>{category.icon}</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: C.ink }}>
                    {category.name}
                  </span>
                  <Box on={isFullySelected} part={isPartiallySelected} />
                </div>

                <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {categoryModules.map((module) => {
                    const on = selectedModules.includes(module.moduleId);
                    return (
                      <div
                        key={module.moduleId}
                        className={`tm-check ${on ? "is-on" : ""}`}
                        onClick={() => this.handleModuleChange(module.moduleId)}
                      >
                        <Box on={on} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, color: C.ink }}>{module.moduleName}</div>
                          {module.description && (
                            <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>
                              {module.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // --- Modals ---------------------------------------------------------------

  renderMemberModal() {
    const { memberId } = this.state;

    return (
      <Modal isOpen={this.state.modal} toggle={this.toggle} size="xl" className="con-modal" centered>
        <div style={{ padding: "26px 28px 28px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 21, fontWeight: 500, color: C.ink }}>
                {memberId ? "Edit team member" : "Invite a team member"}
              </div>
              <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>
                {memberId
                  ? "Change their details. Permissions are managed separately."
                  : "They sign in with this email and password, and see only the modules you grant."}
              </div>
            </div>
            <button
              type="button"
              onClick={this.toggle}
              aria-label="Close"
              style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 4, lineHeight: 1 }}
            >
              <span className="ms" style={{ fontSize: 22 }}>close</span>
            </button>
          </div>

          <AvForm onValidSubmit={this.handleSubmit} className="mt-4">
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>First name</Label>
                  <AvField
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={this.state.firstName}
                    onChange={this.handleChange}
                    required
                    placeholder="Amina"
                                  errorMessage="First name is required"
                                />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Last name</Label>
                  <AvField
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={this.state.lastName}
                    onChange={this.handleChange}
                    required
                    placeholder="Mwamba"
                                  errorMessage="Last name is required"
                                />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Email</Label>
                  <AvField
                    type="email"
                    id="email"
                    name="email"
                    value={this.state.email}
                    onChange={this.handleChange}
                    required
                    placeholder="name@hotellubumbashi.cd"
                                  errorMessage="Email is required"
                                />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Mobile</Label>
                  <AvField
                    type="number"
                    id="mobile"
                    name="mobile"
                    value={this.state.mobile}
                    onChange={this.handleChange}
                    required
                    placeholder="243800000000"
                                  errorMessage="Mobile is required"
                                />
                </FormGroup>
              </Col>
            </Row>

            {!memberId && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Password</Label>
                    <AvField
                      type="password"
                      id="password"
                      name="password"
                      value={this.state.password}
                      onChange={this.handleChange}
                      required
                      minLength={6}
                      placeholder="Minimum 6 characters"
                                  errorMessage="Password is required"
                                />
                  </FormGroup>
                </Col>
                {/* A branch admin cannot choose a branch: the server pins the
                    new member to the creator's own branch. */}
                {!isBranchScopedAdmin() && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Branch access</Label>
                      <Input
                        type="select"
                        id="branchId"
                        value={this.state.branchId}
                        onChange={this.handleChange}
                      >
                        <option value="">All branches (tenant-wide)</option>
                        {this.state.branches.map((b) => (
                          <option key={b._id} value={b._id}>
                            {b.name}
                          </option>
                        ))}
                      </Input>
                      <div style={{ marginTop: 6, fontSize: 12, color: C.taupeLight }}>
                        Scope this member to a single branch, or leave as all branches.
                      </div>
                    </FormGroup>
                  </Col>
                )}
              </Row>
            )}

            {/* Access level. Separate from module permissions:
                those decide WHAT a member can see, this decides
                whether they can CHANGE it. Enforced server-side —
                a view-only account's writes are rejected with 403
                regardless of what the UI shows. */}
            {!memberId && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Access level</Label>
                    <Input
                      type="select"
                      id="accessLevel"
                      value={this.state.accessLevel}
                      onChange={this.handleChange}
                    >
                      <option value="full">Full access — can create, edit and delete</option>
                      <option value="view">View only — Branch Staff, read-only</option>
                    </Input>
                    <div style={{ marginTop: 6, fontSize: 12, color: C.taupeLight }}>
                      {this.state.accessLevel === "view"
                        ? "This member will see the modules you grant below, but cannot change anything."
                        : "This member can modify anything in the modules you grant below."}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            )}

            {!memberId && <div className="mt-4">{this.renderPermissionCategories()}</div>}

            <div style={{ marginTop: 26, display: "flex", gap: 12 }}>
              <DialogBtn onClick={this.toggle}>Cancel</DialogBtn>
              <DialogBtn kind="primary" type="submit">
                {memberId ? "Save changes" : "Create member"}
              </DialogBtn>
            </div>
          </AvForm>
        </div>
      </Modal>
    );
  }

  renderPermissionModal() {
    return (
      <Modal
        isOpen={this.state.permissionModal}
        toggle={this.togglePermission}
        size="xl"
        className="con-modal"
        centered
      >
        <div style={{ padding: "26px 28px 28px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 21, fontWeight: 500, color: C.ink }}>Module permissions</div>
              <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>
                Decide which parts of the console this member can open.
              </div>
            </div>
            <button
              type="button"
              onClick={this.togglePermission}
              aria-label="Close"
              style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 4, lineHeight: 1 }}
            >
              <span className="ms" style={{ fontSize: 22 }}>close</span>
            </button>
          </div>

          <div style={{ marginTop: 22 }}>{this.renderPermissionCategories()}</div>

          <div style={{ marginTop: 26, display: "flex", gap: 12 }}>
            <DialogBtn onClick={this.togglePermission}>Cancel</DialogBtn>
            <DialogBtn kind="primary" onClick={this.handlePermissionSubmit}>
              Save permissions
            </DialogBtn>
          </div>
        </div>
      </Modal>
    );
  }

  renderPasswordModal() {
    const s = this.state;
    const tooShort = s.newPassword && s.newPassword.length < 6;
    const mismatch = s.confirmPassword && s.newPassword !== s.confirmPassword;

    return (
      <Modal isOpen={s.passwordModal} toggle={this.togglePassword} className="con-modal" centered>
        <div style={{ padding: 30 }}>
          {!s.passwordResetSuccess ? (
            <React.Fragment>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 21, fontWeight: 500, color: C.ink }}>Reset password</div>
                  {s.resetMemberName && (
                    <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>
                      For {s.resetMemberName}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={this.togglePassword}
                  aria-label="Close"
                  style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 4, lineHeight: 1 }}
                >
                  <span className="ms" style={{ fontSize: 22 }}>close</span>
                </button>
              </div>

              <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 16 }}>
                <Field label="New password" error={tooShort ? "At least 6 characters" : null}>
                  <div style={{ position: "relative" }}>
                    <input
                      className="tm-pw"
                      type={s.showNewPassword ? "text" : "password"}
                      id="newPassword"
                      value={s.newPassword}
                      onChange={this.handleChange}
                      placeholder="Minimum 6 characters"
                    />
                    <button
                      type="button"
                      className="tm-eye"
                      title={s.showNewPassword ? "Hide password" : "Show password"}
                      onClick={() => this.setState({ showNewPassword: !s.showNewPassword })}
                    >
                      <span className="ms" style={{ fontSize: 19 }}>
                        {s.showNewPassword ? "visibility_off" : "visibility"}
                      </span>
                    </button>
                  </div>
                </Field>

                <Field
                  label="Confirm password"
                  error={mismatch ? "Passwords do not match" : null}
                  hint={
                    s.confirmPassword && !mismatch && s.newPassword.length >= 6
                      ? "Passwords match"
                      : null
                  }
                >
                  <div style={{ position: "relative" }}>
                    <input
                      className="tm-pw"
                      type={s.showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={s.confirmPassword}
                      onChange={this.handleChange}
                      placeholder="Re-enter password"
                    />
                    <button
                      type="button"
                      className="tm-eye"
                      title={s.showConfirmPassword ? "Hide password" : "Show password"}
                      onClick={() => this.setState({ showConfirmPassword: !s.showConfirmPassword })}
                    >
                      <span className="ms" style={{ fontSize: 19 }}>
                        {s.showConfirmPassword ? "visibility_off" : "visibility"}
                      </span>
                    </button>
                  </div>
                </Field>
              </div>

              <div style={{ marginTop: 26, display: "flex", gap: 12 }}>
                <DialogBtn onClick={this.togglePassword}>Cancel</DialogBtn>
                <DialogBtn
                  kind="primary"
                  onClick={this.handlePasswordSubmit}
                  disabled={!s.newPassword || s.newPassword.length < 6 || s.newPassword !== s.confirmPassword}
                >
                  Reset password
                </DialogBtn>
              </div>
            </React.Fragment>
          ) : (
            <div style={{ textAlign: "center" }}>
              <span
                className="ms"
                style={{ fontSize: 30, color: "#2f7d33", background: "rgba(76,175,80,.14)", padding: 16, borderRadius: 20 }}
              >
                check_circle
              </span>
              <div style={{ marginTop: 20, fontSize: 21, fontWeight: 500, color: C.ink }}>
                Password reset
              </div>
              <div style={{ marginTop: 10, fontSize: 15, color: C.slate, lineHeight: 1.55 }}>
                Share it with {s.resetMemberName || "them"} over a private channel. They
                should change it after signing in.
              </div>

              <div
                style={{
                  marginTop: 22, display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px", borderRadius: 14, background: "#fbf0dd", textAlign: "left",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.taupe }}>New password</div>
                  <div style={{ fontFamily: "monospace", fontSize: 16, color: C.ink, wordBreak: "break-all" }}>
                    {s.showNewPassword ? s.newPassword : "••••••••"}
                  </div>
                </div>
                <IconBtn
                  icon={s.showNewPassword ? "visibility_off" : "visibility"}
                  title={s.showNewPassword ? "Hide password" : "Show password"}
                  onClick={() => this.setState({ showNewPassword: !s.showNewPassword })}
                />
                <IconBtn icon="content_copy" title="Copy password" onClick={this.copyPasswordToClipboard} />
              </div>

              <div style={{ marginTop: 26 }}>
                <DialogBtn kind="primary" onClick={this.togglePassword}>Done</DialogBtn>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  render() {
    const s = this.state;
    const write = canWrite();
    const scoped = isBranchScopedAdmin();

    return (
      <ConsolePage
        title="Team & access"
        subtitle={
          s.isLoaded
            ? `${s.items.length} member${s.items.length === 1 ? "" : "s"}`
            : "Loading the team…"
        }
        actions={
          <React.Fragment>
            {isSuperAdmin() && (
              <Btn kind="quiet" icon="history" onClick={() => this.props.history.push("/activity-logs")}>
                Activity log
              </Btn>
            )}
            {write && (
              <Btn icon="person_add" onClick={this.toggle}>
                Invite member
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div
          style={{
            // 1.6fr left the members table 830px wide in an 808px column, so
            // the last row action (delete) was clipped at the panel edge - it
            // scrolled, but nothing signalled that. 2.2fr gives the five
            // columns plus four action buttons room to sit.
            display: "grid", gridTemplateColumns: "minmax(0,2.2fr) minmax(0,1fr)",
            gap: 20, alignItems: "start",
          }}
        >
          <Panel style={{ padding: 0 }}>
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "16px 22px", borderBottom: `1px solid ${C.panel}`,
              }}
            >
              <div>
                <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>Members</div>
                <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>
                  {scoped
                    ? "Everyone assigned to your branch"
                    : "Everyone with a console sign-in for this hotel"}
                </div>
              </div>
              {/* A branch admin has no scope to switch — the server returns
                  only their own branch either way. */}
              {!scoped && (
                <select
                  className="tm-sel"
                  value={s.branchFilter}
                  onChange={this.handleBranchFilterChange}
                >
                  <option value="">All branches</option>
                  {s.branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div style={{ padding: "0 10px" }}>
              {!s.isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading the team…
                </div>
              ) : (
                <Table
                  columns={this.columns()}
                  rows={s.items}
                  empty="No team members yet — invite the first one"
                />
              )}
            </div>
          </Panel>

          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {this.renderBranches()}
            {this.renderAccessSummary()}
          </div>
        </div>

        {this.renderMemberModal()}
        {this.renderPermissionModal()}
        {this.renderPasswordModal()}

        <ConfirmDialog
          isOpen={s.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={this.confirmDelete}
          icon="person_remove"
          title="Remove this team member?"
          body="They lose access to the console immediately. This cannot be undone."
          confirmText="Remove member"
          loading={s.deleteLoading}
        />
      </ConsolePage>
    );
  }
}

export default TeamList;
